import { EllipsisVertical, Mic, PenSquare, Plus, Send, Smile, Users } from "lucide-react";
import { useState } from "react";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { getPerson, MOCK_CONVERSATIONS, type MockConversation, VIEWER_ID } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockTabs, UnreadBadge } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

function ConversationAvatar({ conversation, size }: { conversation: MockConversation; size: number }) {
	const { palette } = useCommunityVariant();
	if (conversation.personId) {
		return <MockAvatar person={getPerson(conversation.personId)} size={size} presence={conversation.online ? "online" : undefined} />;
	}
	return (
		<span
			aria-hidden="true"
			className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white"
			style={{ width: size, height: size, background: palette.accent, fontSize: Math.round(size * 0.36) }}
		>
			{conversation.initials}
		</span>
	);
}

interface WebMessagesScreenProps {
	interactive?: boolean;
	compact?: boolean;
	initialConversationId?: string;
}

export function WebMessagesScreen({ interactive, compact, initialConversationId = "c1" }: WebMessagesScreenProps) {
	const { palette } = useCommunityVariant();
	const [activeId, setActiveId] = useState(initialConversationId);
	const active = MOCK_CONVERSATIONS.find((conversation) => conversation.id === activeId) ?? MOCK_CONVERSATIONS[0];
	if (!active) return null;

	return (
		<div className="flex h-full min-h-0 bg-white" data-mock="messages">
			<aside
				className={`hidden shrink-0 flex-col border-r sm:flex ${compact ? "w-44" : "w-60"}`}
				style={{ borderColor: MOCK_TOKENS.border }}
				data-mock="conversation-list"
			>
				<div className="flex items-center justify-between border-b px-2 py-1.5" style={{ borderColor: MOCK_TOKENS.border }}>
					<MockTabs items={["Chats", "Contacts"]} active={0} />
					<span className="flex items-center gap-1 text-neutral-600" aria-hidden="true">
						<span className="inline-flex size-6 items-center justify-center rounded-lg" title="New chat">
							<PenSquare className="size-3.5" />
						</span>
						<span className="inline-flex size-6 items-center justify-center rounded-lg" title="New group chat">
							<Users className="size-3.5" />
						</span>
					</span>
				</div>
				<ul className="mock-scroll min-h-0 flex-1 overflow-y-auto">
					{MOCK_CONVERSATIONS.map((conversation) => {
						const isActive = conversation.id === active.id;
						const inner = (
							<>
								<ConversationAvatar conversation={conversation} size={32} />
								<span className="min-w-0 flex-1">
									<span className="flex items-center justify-between gap-1">
										<span className="truncate text-[11px] font-semibold text-neutral-900">{conversation.name}</span>
										<span className="shrink-0 text-[9px] text-neutral-500">{conversation.time}</span>
									</span>
									<span className="flex items-center justify-between gap-1">
										<span className="truncate text-[10px] text-neutral-500">{conversation.lastMessage}</span>
										{conversation.unread ? <UnreadBadge count={conversation.unread} tone="accent" /> : null}
									</span>
								</span>
							</>
						);
						const classes = "flex w-full items-center gap-2 px-2.5 py-2 text-left";
						const style = isActive ? { background: palette.accentSoft } : undefined;
						return (
							<li key={conversation.id} data-mock="conversation-row">
								{interactive ? (
									<button
										type="button"
										onClick={() => setActiveId(conversation.id)}
										aria-current={isActive ? "true" : undefined}
										className={`${classes} hover:bg-neutral-50`}
										style={style}
									>
										{inner}
									</button>
								) : (
									<span className={classes} style={style} aria-hidden="true">
										{inner}
									</span>
								)}
							</li>
						);
					})}
				</ul>
			</aside>

			<section className="flex min-w-0 flex-1 flex-col" data-mock="chat-pane">
				<header className="flex items-center gap-2 border-b px-3 py-2" style={{ borderColor: MOCK_TOKENS.border }}>
					<ConversationAvatar conversation={active} size={28} />
					<div className="min-w-0 flex-1">
						<p className="truncate text-[11px] font-semibold text-neutral-900">{active.name}</p>
						<p className="text-[10px] text-neutral-500">
							{active.kind === "dm" ? (active.online ? "Online" : "Last seen recently") : `${active.messages.length + 2} participants`}
						</p>
					</div>
					<EllipsisVertical className="size-4 text-neutral-500" aria-hidden="true" />
				</header>
				<div
					className="mock-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-3"
					style={{ background: MOCK_TOKENS.background }}
				>
					<span className="mx-auto rounded-full bg-white px-2 py-0.5 text-[9px] font-medium text-neutral-500" aria-hidden="true">
						Today
					</span>
					{active.messages.map((message) => {
						const mine = message.senderId === VIEWER_ID;
						const sender = getPerson(message.senderId);
						return (
							<div key={message.id} className={`flex items-end gap-1.5 ${mine ? "flex-row-reverse" : ""}`} data-mock="message-bubble">
								{mine ? null : <MockAvatar person={sender} size={20} />}
								<div className={`flex max-w-[78%] flex-col ${mine ? "items-end" : "items-start"}`}>
									{!mine && active.kind === "group" ? <span className="mb-0.5 text-[9px] text-neutral-500">{sender.name}</span> : null}
									<span
										className={`rounded-2xl px-2.5 py-1.5 text-[11px] leading-snug ${mine ? "rounded-br-md text-white" : "rounded-bl-md bg-white text-neutral-800"}`}
										style={mine ? { background: palette.accent } : { boxShadow: MOCK_TOKENS.surfaceShadow }}
									>
										{message.text}
									</span>
									<span className="mt-0.5 text-[9px] text-neutral-400">{message.time}</span>
								</div>
							</div>
						);
					})}
				</div>
				<footer className="border-t px-3 py-2" style={{ borderColor: MOCK_TOKENS.border }} data-mock="composer">
					<div className="flex items-center gap-2 rounded-xl border px-2.5 py-1.5" style={{ borderColor: MOCK_TOKENS.border }}>
						<Plus className="size-4 text-neutral-500" aria-hidden="true" />
						<span className="flex-1 text-[11px] text-neutral-400">Enter your message</span>
						<Smile className="size-4 text-neutral-500" aria-hidden="true" />
						<Mic className="size-4 text-neutral-500" aria-hidden="true" />
						<span
							className="inline-flex size-6 items-center justify-center rounded-lg text-white"
							style={{ background: palette.accent }}
							aria-hidden="true"
						>
							<Send className="size-3" />
						</span>
					</div>
				</footer>
			</section>
		</div>
	);
}
